import { useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { Food } from "@corpuscomplete/core/nutrition/food";
import { FoodCategory } from "@corpuscomplete/core/nutrition/food-category";
import { FoodCategoryUI } from "@corpuscomplete/core-ui/nutrition/food-category";
import { FoodSearchUI } from "@corpuscomplete/core-ui/nutrition/food/search";
import { Button } from "@corpuscomplete/web-ui/components/button";
import { Command } from "@corpuscomplete/web-ui/components/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@corpuscomplete/web-ui/components/dialog";
import { Input } from "@corpuscomplete/web-ui/components/input";
import { Popover, PopoverContent, PopoverTrigger } from "@corpuscomplete/web-ui/components/popover";
import { Text } from "@corpuscomplete/web-ui/components/text";
import { cn } from "@corpuscomplete/web-ui/lib/utils";
import { CheckIcon, ChevronDownIcon, FilterIcon, SearchIcon, XIcon } from "lucide-react";
import {
  FoodSearchDialogResults,
  type FoodSearchDialogResultsHandle,
} from "./food-search-dialog-results";

const MIN_SEARCH_LENGTH = 3;

type CategoryOption = ReturnType<typeof FoodCategoryUI.useSearch>[number];
type CategoryFilterArgs = Parameters<FoodSearchUI.SearchState["addCategoryFilter"]>[0];
type CategoryFilterMode = CategoryFilterArgs["mode"];
const CATEGORY_FILTER_ASSOCIATION = "contains" satisfies CategoryFilterArgs["association"];
const CATEGORY_FILTER_MODES = ["include", "exclude"] as const;

type CategoryGroupToken = {
  mode: CategoryFilterMode;
  ids: string[];
  labels: string[];
};

type CategoryFilterGroup = {
  mode: CategoryFilterMode;
  categoryIds: string[];
};

export type FoodSearchDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchState: FoodSearchUI.SearchState;
  onSelectItem: (item: Food.Item) => void;
  title?: string;
  description?: string;
  placeholder?: string;
  className?: string;
  renderShortQueryContent?: () => ReactNode;
  renderSidePanel?: () => ReactNode;
  renderFooter?: () => ReactNode;
};

export function FoodSearchDialog({
  open,
  onOpenChange,
  searchState,
  onSelectItem,
  title = "Search foods",
  description = "Search foods and refine results with filters.",
  placeholder = "Search for foods",
  className,
  renderShortQueryContent,
  renderSidePanel,
  renderFooter,
}: FoodSearchDialogProps) {
  const resultsRef = useRef<FoodSearchDialogResultsHandle>(null);
  const categoryGroups = useCategoryFilterGroups(searchState.categories);
  const { sections, isLoading } = FoodSearchUI.useSearch({
    text: searchState.text,
    categories: searchState.categories,
    showAllResults: true,
  });
  const showSearchResults = searchState.text.length >= MIN_SEARCH_LENGTH;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg">
      <DialogHeader className="sr-only">
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{description}</DialogDescription>
      </DialogHeader>
      <DialogContent
        showCloseButton={false}
        containerClassName="flex items-center [--dialog-bottom-gap:4vh] [--dialog-top-gap:4vh] sm:[--dialog-bottom-gap:4vh] sm:[--dialog-top-gap:4vh]"
        className={cn(
          "h-[calc(100dvh-var(--dialog-top-gap)-var(--dialog-bottom-gap))] overflow-hidden gap-0 border-[#d8dce3] bg-[#f7f8fa] p-0 shadow-2xl sm:h-[min(54rem,92vh)] sm:max-w-[92rem]",
          className,
        )}
      >
        <Command shouldFilter={false} className="flex h-full min-h-0 flex-col bg-transparent">
          <header className="border-b border-[#e1e4ea] bg-white px-3 py-1.5 sm:px-4">
            <div className="flex items-center gap-2">
              <div className="relative min-w-0 flex-1">
                <SearchIcon className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-[#6b7280]" />
                <Input
                  value={searchState.text}
                  onChange={(event) => searchState.onChangeText(event.target.value)}
                  onKeyDownCapture={(event) => resultsRef.current?.handleKeyDown(event)}
                  autoFocus
                  placeholder={placeholder}
                  className="h-7 rounded-md border-[#d8dce3] bg-[#fbfbfc] pr-3 pl-8 text-sm shadow-none focus-visible:bg-white md:text-xs/relaxed"
                />
              </div>
              <AddFilterMenu searchState={searchState} />
              <Button
                variant="ghost"
                size="icon-sm"
                className="size-7 rounded-md text-[#6b7280] hover:bg-[#f0f2f5]"
                onClick={() => onOpenChange(false)}
              >
                <XIcon className="size-3.5" />
                <span className="sr-only">Close</span>
              </Button>
            </div>
          </header>

          <GroupedFilterBar searchState={searchState} groups={categoryGroups} />

          <div
            className={cn(
              "grid min-h-0 flex-1 gap-0 bg-[#f7f8fa]",
              renderSidePanel
                ? "grid-rows-[minmax(0,1fr)_minmax(0,16rem)] lg:grid-cols-[minmax(0,1fr)_24rem] lg:grid-rows-none"
                : "grid-cols-1",
            )}
          >
            <main
              className={cn(
                "min-h-0 min-w-0 overflow-hidden bg-[#fbfbfc]",
                renderSidePanel ? "border-b border-[#e1e4ea] lg:border-r lg:border-b-0" : null,
              )}
            >
              <div className="h-full overflow-y-auto overscroll-contain">
                {!showSearchResults ? (
                  renderShortQueryContent ? (
                    renderShortQueryContent()
                  ) : searchState.text.length > 0 ? (
                    <EmptyPanel>Type at least 3 characters to search foods.</EmptyPanel>
                  ) : (
                    <EmptyPanel>Search foods or add filters to narrow the result set.</EmptyPanel>
                  )
                ) : (
                  <FoodSearchDialogResults
                    ref={resultsRef}
                    sections={sections}
                    isLoading={isLoading}
                    onSelectItem={onSelectItem}
                    className="h-full"
                  />
                )}
              </div>
            </main>

            {renderSidePanel ? (
              <aside className="flex min-h-0 flex-col overflow-hidden bg-white">
                {renderSidePanel()}
              </aside>
            ) : null}
          </div>

          {renderFooter ? (
            <div className="border-t border-[#e1e4ea] bg-white p-1">{renderFooter()}</div>
          ) : null}
        </Command>
      </DialogContent>
    </Dialog>
  );
}

function GroupedFilterBar({
  searchState,
  groups,
}: {
  searchState: FoodSearchUI.SearchState;
  groups: CategoryFilterGroup[];
}) {
  const tokens = useCategoryGroupTokens(groups);

  if (tokens.length === 0) {
    return null;
  }

  return (
    <section className="border-b border-[#e1e4ea] bg-white px-4 py-2.5 sm:px-5">
      <div className="flex items-center gap-1 overflow-x-auto pb-0.5">
        {tokens.map((token) => (
          <CategoryGroupSegmentToken key={token.mode} token={token} searchState={searchState} />
        ))}
        <Button
          variant="ghost"
          size="sm"
          className="ml-auto h-8 shrink-0 rounded-lg px-2 text-xs text-[#6b7280] opacity-70 hover:opacity-100"
          onClick={() => {
            searchState.clearCategoryFilters();
          }}
        >
          Clear all
        </Button>
      </div>
    </section>
  );
}

function CategoryGroupSegmentToken({
  token,
  searchState,
}: {
  token: CategoryGroupToken;
  searchState: FoodSearchUI.SearchState;
}) {
  const [valuePickerOpen, setValuePickerOpen] = useState(false);
  const [visibleEditIds, setVisibleEditIds] = useState<string[]>(token.ids);
  const [draftEditIds, setDraftEditIds] = useState<string[]>(token.ids);

  return (
    <div className="inline-flex h-8 shrink-0 items-center overflow-hidden rounded-lg border border-[#d8dce3] bg-[#f7f8fa] text-xs shadow-xs">
      <span className="border-r border-[#d8dce3] px-2.5 font-medium text-[#4b5563]">Category</span>
      <Popover modal={false}>
        <PopoverTrigger className="inline-flex h-full items-center gap-1 border-r border-[#d8dce3] px-2.5 font-medium text-[#111827] hover:bg-white">
          {operatorLabel(token.mode)}
          <ChevronDownIcon className="size-3" />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-40 rounded-xl border-[#d8dce3] p-0.5 shadow-xl"
          data-category-filter-popover
        >
          {CATEGORY_FILTER_MODES.map((mode) => (
            <button
              key={mode}
              type="button"
              className="flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left text-sm hover:bg-[#f3f4f6]"
              onClick={() => {
                if (mode === token.mode) return;
                switchCategoryGroup(searchState, token.mode, mode);
              }}
            >
              {operatorLabel(mode)}
              {mode === token.mode ? <CheckIcon className="size-3.5" /> : null}
            </button>
          ))}
        </PopoverContent>
      </Popover>
      <Popover
        open={valuePickerOpen}
        onOpenChange={(open) => {
          if (open) {
            setVisibleEditIds(token.ids);
            setDraftEditIds(token.ids);
          } else {
            applyCategoryGroupSelection(searchState, token.mode, visibleEditIds, draftEditIds);
          }
          setValuePickerOpen(open);
        }}
        modal={false}
      >
        <PopoverTrigger className="inline-flex h-full max-w-56 items-center gap-1 border-r border-[#d8dce3] px-2.5 font-semibold text-[#111827] hover:bg-white">
          <span className="truncate">{categorySummary(token.labels)}</span>
          <ChevronDownIcon className="size-3 shrink-0" />
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[min(30rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border-[#d8dce3] bg-white p-0 shadow-2xl"
          data-category-filter-popover
        >
          <EditCategoryValuePicker
            selectedIds={draftEditIds}
            visibleIds={visibleEditIds}
            onToggleCategory={(categoryId) =>
              setDraftEditIds((ids) => toggleCategoryIds(ids, categoryId))
            }
            title="Edit category group"
          />
        </PopoverContent>
      </Popover>
      <button
        type="button"
        aria-label={`Remove ${operatorLabel(token.mode)} category filter`}
        className="flex h-full w-8 items-center justify-center text-[#6b7280] hover:bg-white hover:text-[#111827]"
        onClick={() => clearCategoryGroup(searchState, token.mode)}
      >
        <XIcon className="size-3.5" />
      </button>
    </div>
  );
}

function AddFilterMenu({ searchState }: { searchState: FoodSearchUI.SearchState }) {
  const [open, setOpen] = useState(false);
  const mode = "include";

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger
        render={
          <button
            type="button"
            className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md border border-[#d8dce3] bg-white px-2.5 text-xs font-medium text-[#111827] shadow-xs hover:bg-[#f7f8fa]"
          />
        }
      >
        <FilterIcon className="size-3.5" />
        Filter categories
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[min(30rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border-[#d8dce3] bg-white p-0 shadow-2xl"
        data-category-filter-popover
      >
        <AddCategoryValuePicker
          selectedIds={categoryIdsForMode(searchState.categories, mode)}
          onToggleCategory={(categoryId) => toggleCategoryFilter(searchState, mode, categoryId)}
          title="Category"
        />
      </PopoverContent>
    </Popover>
  );
}

function AddCategoryValuePicker({
  selectedIds,
  onToggleCategory,
  title,
}: {
  selectedIds: string[];
  onToggleCategory: (categoryId: string) => void;
  title: string;
}) {
  const [categoryText, setCategoryText] = useState("");
  const commonCategories = FoodCategoryUI.useByKeys(FoodCategory.commonKeys);
  const selectedCategories = FoodCategoryUI.useByIds(selectedIds);
  const categoryResults = FoodCategoryUI.useSearch(categoryText, { limit: 12 });
  const availableCategories = sortCategories(
    categoryText.trim() ? categoryResults : commonCategories,
  );
  const categories = mergePinnedCategories(selectedCategories, availableCategories);
  const selectedCategoryIds = new Set(selectedCategories.map((category) => category.id));
  const showPinnedDivider =
    selectedCategories.length > 0 && categories.length > selectedCategories.length;
  const firstCategory = categories[0];

  return (
    <div className="min-w-0 p-3">
      <div className="mb-3 flex items-center justify-between gap-3">
        <Text variant="sm" className="font-semibold text-[#111827]">
          {title}
        </Text>
      </div>
      <div className="relative mb-3">
        <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-[#6b7280]" />
        <Input
          value={categoryText}
          onChange={(event) => setCategoryText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== "Enter" || !firstCategory) return;
            event.preventDefault();
            onToggleCategory(firstCategory.id);
          }}
          placeholder="Search category values"
          className="h-9 rounded-xl border-[#d8dce3] bg-[#fbfbfc] pl-8 text-sm shadow-none"
        />
      </div>
      <div className="max-h-72 overflow-y-auto rounded-xl border border-[#e1e4ea] bg-white">
        {categories.length > 0 ? (
          categories.map((category, index) => {
            const checked = selectedIds.includes(category.id);
            const showDividerBefore =
              showPinnedDivider &&
              index === selectedCategories.length &&
              !selectedCategoryIds.has(category.id);
            return (
              <div key={category.id}>
                {showDividerBefore ? <div className="border-t border-[#e1e4ea]" /> : null}
                <CategoryOptionRow
                  category={category}
                  checked={checked}
                  onToggleCategory={onToggleCategory}
                />
              </div>
            );
          })
        ) : (
          <div className="px-3 py-8 text-center text-sm text-[#6b7280]">No categories found.</div>
        )}
      </div>
    </div>
  );
}

function EditCategoryValuePicker({
  selectedIds,
  visibleIds,
  onToggleCategory,
  title,
}: {
  selectedIds: string[];
  visibleIds: string[];
  onToggleCategory: (categoryId: string) => void;
  title: string;
}) {
  const visibleCategories = FoodCategoryUI.useByIds(visibleIds);
  const categories = sortCategories(visibleCategories);

  return (
    <div className="min-w-0 p-3">
      <div className="mb-3 flex items-center justify-between gap-3">
        <Text variant="sm" className="font-semibold text-[#111827]">
          {title}
        </Text>
        <Text variant="xs" className="text-[#6b7280]">
          {selectedIds.length} selected
        </Text>
      </div>
      <div className="max-h-72 overflow-y-auto rounded-xl border border-[#e1e4ea] bg-white">
        {categories.length > 0 ? (
          categories.map((category) => {
            const checked = selectedIds.includes(category.id);
            return (
              <CategoryOptionRow
                key={category.id}
                category={category}
                checked={checked}
                onToggleCategory={onToggleCategory}
              />
            );
          })
        ) : (
          <div className="px-3 py-8 text-center text-sm text-[#6b7280]">
            No categories selected.
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryOptionRow({
  category,
  checked,
  onToggleCategory,
}: {
  category: FoodCategory.Info;
  checked: boolean;
  onToggleCategory: (categoryId: string) => void;
}) {
  return (
    <button
      type="button"
      className="flex w-full items-center gap-3 border-b border-[#f0f2f5] px-3 py-2.5 text-left last:border-b-0 hover:bg-[#f7f8fa]"
      onClick={() => onToggleCategory(category.id)}
    >
      <span
        className={cn(
          "flex size-4 items-center justify-center rounded border",
          checked ? "border-[#5e6ad2] bg-[#5e6ad2] text-white" : "border-[#c7ccd6] bg-white",
        )}
      >
        {checked ? <CheckIcon className="size-3" /> : null}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm font-medium text-[#111827]">
        {category.name}
      </span>
    </button>
  );
}

function useCategoryGroupTokens(groups: CategoryFilterGroup[]) {
  const selectedCategories = FoodCategoryUI.useByIds(groups.flatMap((group) => group.categoryIds));
  const categoryNamesById = useMemo(
    () => new Map(selectedCategories.map((category) => [category.id, category.name])),
    [selectedCategories],
  );

  return useMemo((): CategoryGroupToken[] => {
    return groups.flatMap((group) => {
      const token = categoryGroupToken(group, categoryNamesById);
      return token ? [token] : [];
    });
  }, [categoryNamesById, groups]);
}

function useCategoryFilterGroups(categories: FoodSearchUI.SearchState["categories"]) {
  const containsIncludeIds = categories?.contains?.includeIds ?? [];
  const containsExcludeIds = categories?.contains?.excludeIds ?? [];

  return useMemo((): CategoryFilterGroup[] => {
    const groups: CategoryFilterGroup[] = [
      {
        mode: "include",
        categoryIds: containsIncludeIds,
      },
      {
        mode: "exclude",
        categoryIds: containsExcludeIds,
      },
    ];

    return groups.filter((group) => group.categoryIds.length > 0);
  }, [containsExcludeIds, containsIncludeIds]);
}

function categoryGroupToken(
  group: CategoryFilterGroup,
  categoryNamesById: Map<string, string>,
): CategoryGroupToken | null {
  const categories = group.categoryIds
    .map((id) => ({ id, label: categoryNamesById.get(id) ?? "Category" }))
    .sort((first, second) => first.label.localeCompare(second.label));

  if (categories.length === 0) {
    return null;
  }

  return {
    mode: group.mode,
    ids: categories.map((category) => category.id),
    labels: categories.map((category) => category.label),
  };
}

function categorySummary(labels: string[]) {
  if (labels.length <= 1) {
    return labels[0] ?? "Category";
  }
  if (labels.length === 2) {
    return `${labels[0]}, ${labels[1]}`;
  }
  return `${labels[0]}, ${labels[1]} +${labels.length - 2}`;
}

function operatorLabel(mode: CategoryFilterMode) {
  return mode === "include" ? "contains" : "does not contain";
}

function clearCategoryGroup(searchState: FoodSearchUI.SearchState, mode: CategoryFilterMode) {
  categoryIdsForMode(searchState.categories, mode).forEach((categoryId) =>
    searchState.removeCategoryFilter(categoryFilterArgs(mode, categoryId)),
  );
}

function switchCategoryGroup(
  searchState: FoodSearchUI.SearchState,
  mode: CategoryFilterMode,
  nextMode: CategoryFilterMode,
) {
  categoryIdsForMode(searchState.categories, mode).forEach((categoryId) => {
    searchState.removeCategoryFilter(categoryFilterArgs(mode, categoryId));
    searchState.addCategoryFilter(categoryFilterArgs(nextMode, categoryId));
  });
}

function toggleCategoryFilter(
  searchState: FoodSearchUI.SearchState,
  mode: CategoryFilterMode,
  categoryId: string,
) {
  const isSelected = categoryIdsForMode(searchState.categories, mode).includes(categoryId);
  if (isSelected) {
    searchState.removeCategoryFilter(categoryFilterArgs(mode, categoryId));
    return;
  }

  searchState.addCategoryFilter(categoryFilterArgs(mode, categoryId));
}

function applyCategoryGroupSelection(
  searchState: FoodSearchUI.SearchState,
  mode: CategoryFilterMode,
  previousIds: string[],
  nextIds: string[],
) {
  const nextIdSet = new Set(nextIds);
  const previousIdSet = new Set(previousIds);
  previousIds
    .filter((categoryId) => !nextIdSet.has(categoryId))
    .forEach((categoryId) =>
      searchState.removeCategoryFilter(categoryFilterArgs(mode, categoryId)),
    );
  nextIds
    .filter((categoryId) => !previousIdSet.has(categoryId))
    .forEach((categoryId) => searchState.addCategoryFilter(categoryFilterArgs(mode, categoryId)));
}

function toggleCategoryIds(selectedIds: string[], categoryId: string) {
  return selectedIds.includes(categoryId)
    ? selectedIds.filter((selectedId) => selectedId !== categoryId)
    : [...selectedIds, categoryId];
}

function categoryFilterArgs(mode: CategoryFilterMode, categoryId: string): CategoryFilterArgs {
  return {
    categoryId,
    association: CATEGORY_FILTER_ASSOCIATION,
    mode,
  };
}

function categoryIdsForMode(
  categories: FoodSearchUI.SearchState["categories"],
  mode: CategoryFilterMode,
) {
  const ids =
    mode === "include"
      ? categories?.[CATEGORY_FILTER_ASSOCIATION]?.includeIds
      : categories?.[CATEGORY_FILTER_ASSOCIATION]?.excludeIds;
  return ids ?? [];
}

function mergePinnedCategories(
  pinnedCategories: FoodCategory.Info[],
  availableCategories: FoodCategory.Info[],
) {
  const pinnedIds = new Set(pinnedCategories.map((category) => category.id));
  return [
    ...sortCategories(pinnedCategories),
    ...availableCategories.filter((category) => !pinnedIds.has(category.id)),
  ];
}

function sortCategories(categories: CategoryOption[]) {
  return [...categories].sort((first, second) => first.name.localeCompare(second.name));
}

function EmptyPanel({ children }: { children: ReactNode }) {
  return (
    <div className="pt-[10.5rem] text-center">
      <Text variant="sm" className="mx-auto max-w-sm text-[#6b7280]">
        {children}
      </Text>
    </div>
  );
}
